import { FC, useCallback, useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BasicModal from "../BasicModal";
import { requiredField, invalidPhone } from "helpers/errorMessages";
// import { changePhoneNumber } from "api/user";
import { useFormik } from "formik";
// import { GlobalStore } from "stores";
import countries from "helpers/countries";
import MainChangePhoneStep from "./MainChangePhoneStep";
import SuccessChangePhoneStep from "./SuccessChangePhoneStep";
import type { ModalStepsType } from "helpers/types";
import type { ChangePhoneNumberReq } from "api/types/user";

interface InitialErrors {
  newPhoneNumber?: string;
}
const defaultCountryPref = "United Kingdom";

interface ChangePhoneNumberModalProps {
  isOpen: boolean;
  currentNumber?: string;
  onClose: () => void;
}

const ChangePhoneNumberModal: FC<ChangePhoneNumberModalProps> = ({
  isOpen,
  onClose,
  currentNumber,
}) => {
  const { t } = useTranslation();

  const [oldNumber, setOldNumber] = useState<string>("");
  const [step, setStep] = useState<ModalStepsType>("main");

  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleSubmit,
    handleChange,
    resetForm,
    setFieldValue,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      newPhoneNumber: "",
      phonePref: defaultCountryPref,
    },
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const dialCode = countries.find(
        ({ name }) => name === values.phonePref
      )!.dial_code;

      const personalData: ChangePhoneNumberReq = {
        phoneNumber: `${dialCode} ${values.newPhoneNumber.replaceAll(" ", "")}`,
      };
      console.log(personalData);
      setStep("success");
      // changePhoneNumber(personalData)
      //   .then(() => setStep("success"))
      //   .catch((err) => {
      //     if (err?.response?.data?.message) {
      //       GlobalStore.setError(err.response.data.message);
      //     }
      //   })
      //   .finally(() => setSubmitting(false));
    },
    validate: (values) => {
      const errors: InitialErrors = {};
      if (!values.newPhoneNumber) {
        errors.newPhoneNumber = requiredField;
      }
      if (
        values.newPhoneNumber &&
        (values.newPhoneNumber.length < 4 || values.newPhoneNumber.length > 14)
      ) {
        errors.newPhoneNumber = invalidPhone;
      }
      return errors;
    },
  });

  useEffect(() => {
    if (isOpen && currentNumber) {
      setOldNumber(currentNumber);

      const splitedNumber = currentNumber.split(" ");
      if (splitedNumber.length === 2) {
        const countryDialCode = countries.find(
          ({ dial_code }) => dial_code === splitedNumber[0]
        );
        if (countryDialCode) {
          setFieldValue("phonePref", countryDialCode.name);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handlePhonePrefChange: (val: string) => void = (value) => {
    setFieldValue("phonePref", value);
  };

  const resetState = useCallback(() => {
    resetForm();
    setOldNumber("");
    setStep("main");
  }, [resetForm]);

  const newNumber = useMemo<string>(() => {
    const dialCode = countries.find(
      ({ name }) => name === values.phonePref
    )!.dial_code;

    return `${dialCode} ${values.newPhoneNumber.replaceAll(" ", "")}`;
  }, [values]);

  return (
    <BasicModal
      isOpen={isOpen}
      onExited={resetState}
      onClose={onClose}
      title={t("CHANGE_PHONE_NUMBER")}
      showLoader={isSubmitting}
    >
      {step === "main" && (
        <MainChangePhoneStep
          oldNumber={oldNumber}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          handlePhonePrefChange={handlePhonePrefChange}
          values={values}
          touched={touched}
          errors={errors}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          handleChange={handleChange}
          onClose={onClose}
        />
      )}

      {step === "success" && (
        <SuccessChangePhoneStep
          newNumber={newNumber}
          oldNumber={oldNumber}
          onClose={onClose}
        />
      )}
    </BasicModal>
  );
};

export default ChangePhoneNumberModal;
